import React, { useState, useLayoutEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  TextField,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { modalStyles } from '../styles/modalStyles';
import { LanguageConstants } from '../helpers/LanguageConstants';

// Common country codes for phone numbers
const COUNTRY_CODES = [
  { code: '+1', country: 'US', name: 'United States' },
  { code: '+44', country: 'GB', name: 'United Kingdom' },
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+86', country: 'CN', name: 'China' },
  { code: '+81', country: 'JP', name: 'Japan' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+39', country: 'IT', name: 'Italy' },
  { code: '+34', country: 'ES', name: 'Spain' },
  { code: '+61', country: 'AU', name: 'Australia' },
  { code: '+55', country: 'BR', name: 'Brazil' },
  { code: '+52', country: 'MX', name: 'Mexico' },
  { code: '+27', country: 'ZA', name: 'South Africa' },
  { code: '+971', country: 'AE', name: 'UAE' },
  { code: '+65', country: 'SG', name: 'Singapore' },
  { code: '+60', country: 'MY', name: 'Malaysia' },
  { code: '+66', country: 'TH', name: 'Thailand' },
  { code: '+62', country: 'ID', name: 'Indonesia' },
  { code: '+84', country: 'VN', name: 'Vietnam' },
  { code: '+82', country: 'KR', name: 'South Korea' },
  { code: '+7', country: 'RU', name: 'Russia' },
  { code: '+20', country: 'EG', name: 'Egypt' },
  { code: '+234', country: 'NG', name: 'Nigeria' },
  { code: '+254', country: 'KE', name: 'Kenya' },
  { code: '+92', country: 'PK', name: 'Pakistan' },
  { code: '+880', country: 'BD', name: 'Bangladesh' },
  { code: '+94', country: 'LK', name: 'Sri Lanka' },
  { code: '+977', country: 'NP', name: 'Nepal' },
];

interface AddThreadModalProps {
  open: boolean;
  onClose: () => void;
  onAddThread: (threadData: { name: string; description?: string }) => void;
  isLoading?: boolean;
  messagingProvider?: 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams';
}

const AddThreadModal: React.FC<AddThreadModalProps> = ({ 
  open, 
  onClose, 
  onAddThread, 
  isLoading,
  messagingProvider
}) => {
  const isWhatsApp = messagingProvider === 'whatsapp';
  const [threadName, setThreadName] = useState('');
  const [threadDescription, setThreadDescription] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Clear fields each time the dialog opens — parent closes after create without calling handleClose, so stale values would otherwise remain; layout effect avoids a one-frame flash of old text
  useLayoutEffect(() => {
    if (!open) return;
    setThreadName('');
    setThreadDescription('');
    setCountryCode('+1');
    setPhoneNumber('');
  }, [open]);

  const handleClose = () => {
    setThreadName('');
    setThreadDescription('');
    setCountryCode('+1');
    setPhoneNumber('');
    onClose();
  };

  const handleAddThread = () => {
    if (isWhatsApp) {
      // For WhatsApp, combine country code and phone number
      const fullPhoneNumber = `${countryCode}${phoneNumber.trim()}`;
      if (fullPhoneNumber.replace('+', '').trim()) {
        onAddThread({
          name: fullPhoneNumber,
          description: undefined
        });
      }
    } else {
      if (threadName.trim()) {
        onAddThread({
          name: threadName.trim(),
          description: threadDescription.trim() || undefined
        });
      }
    }
    // Removed handleClose() - modal will be closed by parent component after successful API call
  };

  const isFormValid = isWhatsApp 
    ? phoneNumber.trim() !== '' 
    : threadName.trim() !== '';

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return; // Prevent closing when clicking outside
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: modalStyles.dialog['& .MuiDialog-paper'],
      }}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        <Typography variant="h5" component="span">
          {isWhatsApp ? 'Create New Chat' : `Create New ${LanguageConstants.thread}`}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {isWhatsApp ? (
            <Box>
              <Typography
                component="label"
                sx={{
                  display: 'block',
                  mb: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                Phone Number <span style={{ color: 'red', fontSize: '1.1rem', fontWeight: 'bold' }}>*</span>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <FormControl sx={{ minWidth: 220, maxWidth: 220 }}>
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    renderValue={(value) => {
                      const selectedCountry = COUNTRY_CODES.find(c => c.code === value);
                      if (!selectedCountry) return value;
                      const displayText = `${selectedCountry.name} (${selectedCountry.code})`;
                      return (
                        <Box
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '160px',
                            display: 'block',
                          }}
                        >
                          {displayText}
                        </Box>
                      );
                    }}
                    sx={{
                      height: '56px',
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        paddingRight: '42px !important',
                        paddingLeft: '14px !important',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {COUNTRY_CODES.map((country) => (
                      <MenuItem key={`${country.code}-${country.country}`} value={country.code}>
                        {country.name} ({country.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => {
                    // Only allow numbers
                    const value = e.target.value.replace(/[^\d]/g, '');
                    setPhoneNumber(value);
                  }}
                  placeholder="e.g., 1234567890"
                  required
                  inputProps={{
                    maxLength: 15,
                  }}
                />
              </Box>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                label={`${LanguageConstants.thread} Name `}
                value={threadName}
                onChange={(e) => setThreadName(e.target.value)}
                placeholder={`Enter ${LanguageConstants.thread.toLowerCase()} name`}
                required
              />
              
              <TextField
                fullWidth
                label="Description"
                value={threadDescription}
                onChange={(e) => setThreadDescription(e.target.value)}
                placeholder={`Enter ${LanguageConstants.thread.toLowerCase()} description (optional)`}
                multiline
                rows={4}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAddThread}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Loading...
            </>
          ) : (
            isWhatsApp ? 'Create Chat' : `Create ${LanguageConstants.thread}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddThreadModal; 